import { useEffect, useState } from 'react';

import { useCms } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import { addressFinderSchema } from '@eneco/components/AddressFinder/AddressFinder';
import type { ApiError } from '@eneco/dc/client/ApiError';
import { getWarmthProfile } from '@eneco/dc/services';
import { typedYupResolver } from '@eneco/hooks/form';
import { useRouter } from '@eneco/next';
import type {
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
} from '@monorepo-types/dc';
import { useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';

import type {
  WarmthCheckAddressFinderContent,
  WarmthCheckNotification,
} from './warmth-check-address-finder-content.types';

interface WarmthCheckFormFields {
  postalCode: string;
  houseNumber: string;
  houseNumberSuffix?: string;
}

interface ErrorState {
  title: string;
  content: string;
}

/**
 * Mirrors `libs/sitecore/components/forms/src/WarmCheckerForm/hooks/useWarmChecker.ts` and its `utils/`:
 * look the address up in Digital Core, send the visitor to their profile group's page, or show the
 * notification for the response status.
 */
export const useWarmthCheckAddressFinder = (content: WarmthCheckAddressFinderContent) => {
  const { adapter } = useCms();
  const router = useRouter();
  const [isHouseNumberSuffixRequired, setIsHouseNumberSuffixRequired] = useState(false);
  const [error, setError] = useState<ErrorState | null>(null);

  const formMethods = useForm<WarmthCheckFormFields>({
    mode: 'onBlur',
    resolver: typedYupResolver<WarmthCheckFormFields>(
      yup.object({ ...addressFinderSchema(isHouseNumberSuffixRequired).fields }),
    ),
  });

  const { control, clearErrors } = formMethods;
  const { postalCode, houseNumber, houseNumberSuffix } = useWatch({ control });

  useEffect(() => {
    if (postalCode || houseNumber || houseNumberSuffix) {
      setError(null);
    }
  }, [postalCode, houseNumber, houseNumberSuffix]);

  const toError = (notification?: WarmthCheckNotification): ErrorState => ({
    title: notification?.title || '',
    content: adapter.toRichText(notification?.content) || '',
  });

  const handleFormSubmit = async (formData: WarmthCheckFormFields) => {
    try {
      clearErrors();
      setError(null);

      const response = await getWarmthProfile({
        label: 'eneco' as DC_Repositories_Base_Enumerations_Label,
        businessUnit: 'nl' as DC_Repositories_Base_Enumerations_BusinessUnit,
        postalCode: formData.postalCode,
        houseNumber: Number.parseInt(formData.houseNumber),
        houseNumberSuffix: formData.houseNumberSuffix,
      });

      const profileGroup = response?.data?.profileGroup;
      if (profileGroup) {
        const key = `profile${profileGroup}Link` as keyof WarmthCheckAddressFinderContent;
        const profileLink = linkWithUrl(content[key] as WarmthCheckAddressFinderContent['profile1Link']);
        if (profileLink) router.push(profileLink.href);
      }
    } catch (err) {
      const status = (err as ApiError).status;
      if (status === 400) setError(toError(content.noWarmthNotification));
      else if (status === 404) setError(toError(content.notFoundErrorNotification));
      else setError(toError(content.serverErrorNotification));
    }
  };

  return { formMethods, error, handleFormSubmit, isHouseNumberSuffixRequired, setIsHouseNumberSuffixRequired };
};
